/**
 * Maps each PermissionRole to its directly-granted capabilities.
 *
 * Inherited capabilities are NOT duplicated here -- expansion unions
 * capabilities across the inheritance graph. Each entry lists only the
 * capabilities where that SPECIFIC role is checked in source code.
 */

import type { Capability } from "@remi/enums/capability";
import type { PermissionRole } from "@remi/types/permissions";

export const expandCapabilitiesFromRoles = (expandedRoles: PermissionRole[]): Capability[] => {
  const set = new Set<Capability>();
  for (const role of expandedRoles) {
    for (const cap of RoleCapabilityMapping[role]) {
      set.add(cap);
    }
  }
  return [...set];
};

// AUTONOMOUS_AGENT (below) derives its set from these two arrays, so a capability added here reaches
// that role too — unless it is also listed in AUTONOMOUS_AGENT_EXCLUDED_CAPABILITIES.
const REMI_USER_CAPABILITIES = [
  "actionable.read",
  "activityLog.read",
  "adjuster.create",
  "adjuster.read",
  "ahj.read",
  "aiSummary.generate",
  "aiSummary.view",
  "aiChat.generate",
  "aiBidAnalysis.create",
  "aiBidAnalysis.view",
  "aiFeedback.create",
  "aiFileSummary.view",
  "appeasement.create",
  "appeasement.read",
  "assessment.read",
  "automationUi.read",
  "checklist.update",
  "colorSelection.read",
  "colorSelection.update",
  "bid.read",
  "bid.merge",
  "bid.unmerge",
  "project.owner.update",
  "bid.read.all",
  "bidContact.read",
  "bidProperties.read",
  "bidRequest.read",
  "bidRequest.create",
  "bidRequest.update",
  "bidRequest.updateStatus.all",
  "bidRequest.read.all",
  "bidTable.read",
  "bidTable.update",
  "bill.create",
  "bill.read",
  "bill.read.all",
  "remittance.read",
  "remittance.update",
  "blockingTask.create",
  "buyerData.read",
  "calendar.read",
  "calendar.create",
  "calendar.create.all",
  "calendar.delete",
  "certificateOfCompletion.read",
  "changeOrder.create",
  "changeOrder.read",
  "changeOrder.read.all",
  "changeOrder.readCostBasis",
  "channel.read",
  "contractorLicense.read",
  "contractorOwners.read",
  "contractorOwners.update",
  "contractorProfile.read.all",
  "contractorProfile.create",
  "contractorProfile.update.all",
  "contractorDocuments.uploadCorrection",
  "contractorInternalFields.read",
  "contractorInternalFields.update",
  "conversation.view",
  "conversation.list",
  "conversation.sendMessage",
  "conversation.update",
  "conversation.create",
  "conversation.read.all",
  "conversation.read.organizationLevel",
  "conversationItem.read.all",
  "conversationAssignment.update",
  "conversationClose.update",
  "conversationSubscription.update",
  "county.read",
  "creditMemo.read",
  "deadline.read",
  "dropboxSign.access.all",
  "escalationHub.dismiss",
  "escalationHub.flag",
  "escalationHub.read",
  "escalationHub.readBanner",
  "escalationHub.write",
  "estimate.forceCreate",
  "externalContractor.read",
  "externalContractor.update",
  "file.approveTag",
  "file.read.all",
  "file.upload.cad",
  "proxyPhone.assign",
  "file.access.mobile",
  "file.update.owned",
  "finalInspection.read",
  "funding.read",
  "funding.create",
  "funding.update",
  "escrow.read",
  "financing.read",
  "geographicArea.read",
  "geographicArea.read.serviceAreas",
  "geographicArea.readCountyOverlay",
  "geographicArea.readStateOverlay",
  "globalAppSettings.read",
  "hic.create",
  "holdup.read",
  "homeownerPortal.read",
  "homeownerSentiment.read",
  "hubspot.upsert",
  "hubspot.read",
  "inbox.list",
  "inbox.createView",
  "invoice.create",
  "invoice.read",
  "invoice.viewSummary",
  "invoice.read.all",
  "jobDetails.read",
  "journalEntry.read",
  "link.create",
  "fileAnnotation.read",
  "fileAnnotation.update",
  "livePhotoReview.read",
  "livePhotoReview.update",
  "market.read",
  "materials.update",
  "measurementReport.read",
  "message.channel.view.contractor",
  "message.channel.view.homeowner",
  "message.channel.view.remiInternal",
  "message.channel.view.solarCompany",
  "message.delete.owned",
  "message.restore.owned",
  "message.edit",
  "message.broadcast",
  "message.create",
  "navigation.read.bids",
  "navigation.read.projects",
  "navigation.read.financialConsole",
  "navigation.read.tasks",
  "navigation.read.projectMilestones",
  "navigation.read.tickets",
  "navigation.read.contractors",
  "navigation.read.views",
  "navigation.read.escalations",
  "navigation.read.actionables",
  "navigation.read.insuranceAll",
  "notifications.preferences.read",
  "notifications.preferences.mutate",
  "notifications.read",
  "notifications.mutate",
  "notificationList.read",
  "notificationSettings.view",
  "ntp.accept",
  "opsAssignment.read",
  "organization.read",
  "organization.list",
  "organization.read.all",
  "organization.readType.contractor",
  "organization.readType.funding",
  "organization.readType.homeowner",
  "organization.readType.remiInternal",
  "organization.readType.solarCompany",
  "organization.readType.vendor",
  "partnerFlow.view",
  "partnerFlow.read",
  "partnerFlow.config.read",
  "permittingEntity.view",
  "person.changeOrganization",
  "person.read.basic",
  "person.update.all",
  "person.create",
  "person.delete",
  "person.viewDetails",
  "person.viewUnmaskedPhoneNumber",
  "person.viewProxyPhoneNumber",
  "person.createPerson",
  "person.invite",
  "personGroup.read",
  "personToContractor.read.all",
  "phaseDuration.read",
  "phoneProvider.access",
  "phoneProvider.phones.list",
  "receipt.create",
  "receipt.read",
  "project.read",
  "project.update",
  "project.reevaluate",
  "project.updateEstimateDate",
  "project.photos",
  "project.read.all",
  "projectTable.read",
  "projectStatus.update",
  "projectStatus.alwaysUpdate",
  "projectCategory.read",
  "projectCheckIn.create",
  "projectCheckIn.read",
  "projectCheckIn.delete",
  "projectMilestones.viewAssignmentMilestone",
  "projectMilestones.viewAdvancedFilters",
  "propertyAddress.update",
  "propertyAddress.update.all",
  "propertyAddress.read",
  "quote.applySalesDiscount",
  "quote.create",
  "quote.read",
  "quote.createDealerFee",
  "bidQuote.read.all",
  "quoteVersion.read.all",
  "quoteTab.read",
  "ratebook.read",
  "recruitingTriage.read",
  "recruitingTriage.resolve",
  "region.viewSettings",
  "region.read",
  "regionRole.read",
  "requestForQuote.read",
  "requestForQuote.create",
  "requestForQuote.update",
  "requestForQuote.read.all",
  "review.create.workmanship",
  "review.read",
  "roofScope.read",
  "roofScope.update",
  "salesDetails.read",
  "scopeOfWorkType.read",
  "serviceArea.update",
  "serviceArea.delete",
  "serviceArea.read",
  "smsMessage.delete",
  "source.view",
  // Parity with the legacy flow, where any internal user can close a SOW_CHANGE_REQUEST.
  "sowChange.abandon",
  "sowChange.read",
  "sowChange.updateBlockedProjects",
  "sunrunAutomation.read",
  "tag.view",
  "tag.create",
  "tag.update",
  "tag.delete",
  "task.read",
  "task.read.all",
  "task.update",
  "task.recordAttempt",
  "task.recordAttempt.internalReasons",
  "task.read.contractor",
  "task.update.contractor",
  "task.read.remi",
  "task.update.remi",
  "task.read.automation",
  "task.read.insurance.assessment",
  "task.update.insurance.assessment",
  "task.read.insurance.approval",
  "task.update.insurance.approval",
  "task.read.insurance.estimateReview",
  "task.read.contractorComplaint",
  "task.read.homeowner",
  "task.update.homeowner",
  "task.createPostInstallTask",
  "task.updateAHJRequirements",
  "task.delete",
  "task.review",
  "task.keepAsIs",
  "task.viewAttemptHistory",
  "task.viewTaskMenu",
  "task.orderMaterials.forceComplete",
  "deletedTask.read",
  "deletedTask.update",
  "taskDueDate.update",
  "taskAssignee.update",
  "taskReset.update",
  "taskStatus.update",
  "ticket.view",
  "ticket.create",
  "ticket.update",
  "ticket.list",
  "ticket.note",
  "user.updateStatus",
  "vendorCredit.read",
  "view.read.default",
  "viewList.read",
  "workflow.read",
  "workflow.migrate",
  "reviewBidGuide.read",
  "workOrder.read",
  "workOrder.create",
  "workOrder.update.remi",
  "workOrder.read.all",
  "worksite.read",
  "estimate.create",
  "laborbook.read",
  "mcp.bid.contact.add",
  "mcp.bid.getContacts",
  "mcp.bid.list",
  "mcp.bid.listFiles",
  "mcp.bid.next",
  "mcp.bid.read",
  "mcp.bid.search",
  "mcp.estimate.read",
  "mcp.conversation.addNote",
  "mcp.conversation.assign",
  "mcp.conversation.get",
  "mcp.conversation.getSignals",
  "mcp.conversation.getUnanswered",
  "mcp.conversation.list",
  "mcp.conversation.listQuiet",
  "mcp.conversation.mentionable.list",
  "mcp.conversation.message.react",
  "mcp.conversation.message.reply",
  "mcp.conversation.message.send",
  "mcp.conversation.message.unreact",
  "mcp.conversation.resolve",
  "mcp.conversation.update",
  "mcp.dailyActions.getMyDay",
  "mcp.dailyActions.setCompletion",
  // Granted at REMI_USER (not the narrower REMI_OPS) to mirror the cross-org mcp.file.list
  // grant: every internal-ops role gets read-byte access consistent with its metadata access.
  "mcp.file.getSignedUrls",
  "mcp.file.list",
  "mcp.inbox.bulkReassign",
  "mcp.inbox.bulkResolveConversations",
  "mcp.inbox.bulkSetFollowUp",
  "mcp.inbox.counts",
  "mcp.inbox.search",
  "mcp.notification.dismiss",
  "mcp.notification.get",
  "mcp.notification.list",
  "mcp.notification.markRead",
  "mcp.notification.update",
  "mcp.person.listAssignable",
  "mcp.person.whoami",
  "mcp.phoneCall.get",
  "mcp.phoneCall.getTranscript",
  "mcp.project.get",
  "mcp.project.getBlockers",
  "mcp.project.getSiblings",
  "mcp.project.list",
  "mcp.project.updateStatus",
  "mcp.tag.batchDismiss",
  "mcp.tag.dismiss",
  "mcp.tag.search",
  "mcp.tag.undismiss",
  "mcp.task.attempt.record",
  "mcp.task.attempts.list",
  "mcp.task.list",
  "mcp.task.note.add",
  "mcp.task.note.react",
  "mcp.task.note.reply",
  "mcp.task.note.unreact",
  "mcp.task.reassign",
  "mcp.task.update",
  "mcp.task.updateStatus",
  "mcp.ticket.addComment",
  "mcp.ticket.assign",
  "mcp.ticket.create",
  "mcp.ticket.get",
  "mcp.ticket.getActivity",
  "mcp.ticket.link",
  "mcp.ticket.list",
  "mcp.ticket.listStale",
  "mcp.ticket.note.add",
  "mcp.ticket.note.react",
  "mcp.ticket.note.reply",
  "mcp.ticket.note.unreact",
  "mcp.ticket.tag.add",
  "mcp.ticket.tag.remove",
  "mcp.ticket.update",
  "mcp.ticket.updateStatus",
  "worksiteView.read",
] as const satisfies readonly Capability[];

const REMI_OPS_CAPABILITIES = [
  "escalation.manage",
  "complianceEvent.read",
  "complianceEvent.create",
  "complianceEvent.update",
  "contractorInternalDetailFields.read",
  "contractorInternalDetailFields.update",
  "contractorProfile.manageRoles",
  "escalationHub.createTicket",
  "laborbook.update",
  "laborbook.readDrafts",
  "project.corrections.create",
  "project.corrections.createWarrantyBid",
  "project.solarSiteAssessment.create",
  "project.reinspection.create",
  "bid.warranty.convert",
  "project.qa.assess",
  // Ops-only MCP action gates (direct grant; REMI_ADMIN/ENGINEER/AUTOMATIONS inherit via REMI_OPS).
  "mcp.escalation.addNote",
  "mcp.escalation.dismiss",
  "mcp.escalation.get",
  "mcp.escalation.linkContractor",
  "mcp.escalation.list",
  "mcp.escalation.transition",
  "mcp.escalation.unlinkContractor",
  "mcp.escalation.updateFields",
  "mcp.project.escalate",
  "mcp.team.list",
  "outreach.read",
  "outreach.manage",
] as const satisfies readonly Capability[];

// Message/SMS send capabilities AUTONOMOUS_AGENT must not hold: partner-thread posting and outbound
// text. Everything else in the REMI_USER + REMI_OPS union is granted to it.
const AUTONOMOUS_AGENT_EXCLUDED_CAPABILITIES = new Set<Capability>([
  "message.broadcast",
  "outreach.read",
  "outreach.manage",
  "conversation.message.partner.send",
  "conversation.sms.homeowner.send",
  "conversation.sms.contractor.send",
]);

// The agent-action pair lets Remi accept a Gray Box suggestion on its own, through the same accept path
// an account manager uses, when that suggestion is marked autoEligible.
const AUTONOMOUS_AGENT_CAPABILITIES = [
  ...[...REMI_USER_CAPABILITIES, ...REMI_OPS_CAPABILITIES].filter(
    (capability) => !AUTONOMOUS_AGENT_EXCLUDED_CAPABILITIES.has(capability),
  ),
  "agentAction.read",
  "agentAction.accept",
] as const satisfies readonly Capability[];

export const RoleCapabilityMapping: Record<PermissionRole, readonly Capability[]> = {
  REMI_USER: [
    ...REMI_USER_CAPABILITIES,
    "conversation.message.partner.send",
    "conversation.sms.homeowner.send",
    "conversation.sms.contractor.send",
    "mcp.conversation.text.send",
    "adminTools.trainingAdminTools.manage",
    "contractorTraining.record",
  ],

  REMI_OPS: REMI_OPS_CAPABILITIES,

  REMI_SALES: [
    "bid.create.all",
    "bid.viewCreateBid",
    "project.create",
    "quote.createSalesAdjustment",
    "quote.createDealDeskDiscount",
    "quote.createPriceMatchDiscount",
  ],

  REMI_SALES_ADMIN: [
    "adminTools.salesAdminTools.read",
    "outreach.read",
    "outreach.manage",
    "requestForQuote.forceUpdate",
    "task.forceComplete",
    "taskReset.update.noticeToProceed",
  ],

  REMI_AI_OPERATIONS: ["bidScoring.read"],

  REMI_ADMIN: [
    "escalation.manage",
    "file.update",
    "queueCleanup.readAll",
    "remittance.read.internal",
    // MCP action gate — admin-only per pre-flip permissions: ['REMI_ADMIN'].
    "mcp.project.reassign",
    "agentAction.catchUp",
    "mcp.agentAction.catchUp",
    "message.delete.all",
    "message.restore.all",
    "ntp.accept.upsell",
    "serviceArea.pause.clear",
    "adminTools.read",
    "adminTools.userAdminTools.manage",
    "adminTools.quoteAdminTools.read",
    "adminTools.opsAdminTools.read",
    "adminTools.materialsAdminTools.read",
    "adminTools.materialsAdminTools.update",
    "adminTools.mobileAdminTools.read",
    "adminTools.contractorCapabilityGroupings.read",
    "adminTools.contractorCapabilityGroupings.create",
    "adminTools.contractorCapabilityGroupings.update",
    "adminTools.contractorCapabilityGroupings.delete",
    "adminTools.estimateQaChecks.read",
    "adminTools.estimateQaChecks.create",
    "adminTools.estimateQaChecks.update",
    "adminTools.estimateQaChecks.delete",
    "adminTools.geographicAreaAdminTools.read",
    "adminTools.proxyPhoneAdminTools.read",
    "adminTools.proxyPhoneAdminTools.update",
    "adminTools.telecomMappingAdminTools.read",
    "adminTools.telecomMappingAdminTools.update",
    "adminTools.unknownContactReconciliation.read",
    "adminTools.unknownContactReconciliation.update",
    "adminTools.accountManagementMapAdminTools.read",
    "adminTools.accountManagementMapAdminTools.update",
    "adminTools.woExpiryAdminTools.read",
    "phoneProvider.phones.list",
    "adminTools.portalAdminTools.resetFinancing",
    "adminTools.changeOrderAdminTools.seedLegacyLineItem",
    "assessment.create",
    "assessment.update",
    "bid.readMerged",
    "project.owner.updateAdjuster",
    "bid.warranty.create",
    "project.corrections.create",
    "project.qa.assess",
    "county.create",
    "county.update",
    "funding.cancel",
    "globalAppSettings.update",
    "insurance.viewNonOptimisticStatus",
    "bidScoring.read",
    "colorSelection.reset",
    "navigation.read.admin",
    "opsAssignment.update",
    "personGroup.update",
    "personToContractor.update",
    "projectStatus.alwaysCancel",
    "region.update",
    "region.create",
    "region.delete",
    "region.viewAssignablePersons",
    "regionRole.update",
    "regionRole.create",
    "sunrunAutomation.update",
    "sunrunAutomation.create",
    "task.forceComplete",
    "task.forceDelete",
    "task.update.automation",
    "task.update.workOrderConcessionApproval",
    "user.create",
    "workOrder.update",
    "geographicArea.update.serviceAreas",
    "geographicArea.update.regions",
  ],

  REMI_AUTOMATIONS: [],

  IMPERSONATOR: ["impersonation.create"],

  // adminTools.read is load-bearing: proxy.ts gates every /admin route on it.
  REMI_IMPERSONATION_AUDITOR: ["adminTools.read", "adminTools.impersonationAuditTools.read"],

  REMI_REGIONAL_MANAGER: ["escalationHub.readBanner", "task.orderMaterials.forceComplete", "worksiteView.read"],

  REMI_DEADLINE_MANAGER: ["deadline.read", "deadline.update"],

  REMI_PERMISSIONS_MANAGER: ["permission.read", "permission.write"],

  REMI_CHANGE_ORDER_MANAGER: ["changeOrder.adjustMargin", "task.changeOrder.forceComplete"],

  REMI_WORK_ORDER_MANAGER: [
    "adminTools.read",
    "adminTools.woExpiryAdminTools.read",
    "adminTools.woExpiryAdminTools.update",
  ],

  REMI_DISPATCH: ["navigation.read.dispatch", "project.qa.create", "project.liveVisit.create"],

  REMI_CHANGE_ORDER_SPECIALIST: ["task.update.workOrderReview"],

  // Appeasement review routes its task by PersonGroup lead, not by role, so nothing else grants
  // that lead the capabilities their own REVIEW_APPEASEMENT task needs to be completed.
  REMI_ACCOUNT_MANAGEMENT_LEAD: [
    "worksite.accountManager.transfer",
    "mcp.worksite.setSubstituteAccountManager",
    "appeasement.read",
    "appeasement.approve",
    "appeasement.reject",
    "appeasement.update",
    "colorSelection.reset",
  ],

  // file.update.tag, not file.update: AMs tag files they did not upload (the Retrieve CAD task
  // clears on a CAD_LAYOUT tag) without also gaining file deletion and contractor-document
  // rejection, which share the broader capability. They already hold file.approveTag.
  REMI_ACCOUNT_MANAGER: [
    "project.corrections.create",
    "project.corrections.createWarrantyBid",
    "project.solarSiteAssessment.create",
    "project.reinspection.create",
    "bid.warranty.convert",
    "deadline.update",
    "file.update.tag",
    "colorSelection.reset",
    "agentAction.read",
    "agentAction.request",
    "agentAction.accept",
    "agentAction.dismiss",
  ],

  REMI_QUOTING: ["project.corrections.create", "project.corrections.createWarrantyBid"],

  REMI_CLAIM_CAPTURE_TEAM: ["appeasement.approve", "appeasement.reject", "appeasement.update"],

  // Door-open only — per-tool access comes from other roles.
  REMI_MCP_USER: ["mcp.oauth.access"],
  REMI_MARKETING_WEBSITE: ["marketingLead.create"],

  // Machine principal. Explicit set (no inheritance): the REMI_OPS + REMI_USER union minus the
  // partner-message and outbound-SMS send capabilities. See AUTONOMOUS_AGENT_CAPABILITIES above.
  AUTONOMOUS_AGENT: AUTONOMOUS_AGENT_CAPABILITIES,

  REMI_ENGINEER: [
    "agentAction.read",
    "agentAction.request",
    "agentAction.accept",
    "agentAction.dismiss",
    "adminTools.apiKeyAdminTools.generate",
    "adminTools.apiKeyAdminTools.read",
    "adminTools.apiKeyAdminTools.manage",
    "adminTools.quoteEngineerTools.update",
    "adminTools.propertyModel.read",
    "adminTools.slaDefinitions.edit",
    "adminTools.blockersAdminTools.read",
    "adminTools.anomaliesAdminTools.read",
    "adminTools.anomaliesAdminTools.resolve",
    "adminTools.portalAdminTools.resetFinancing",
    "aiFeedback.viewLink",
    "bid.delete",
    "deadline.update",
    "debug.read",
    "impersonation.create",
    "mcp.execute_sql_query",
    "mcp.worksite.postPartnerUpdate",
    "mcp.worksite.setSubstituteAccountManager",
    "navigation.read.devtools",
    "personGroup.create",
    "personGroup.delete",
    "personGroup.updateKey",
    "project.delete",
    "projectChecklistSchema.read",
    "projectChecklistSchema.create",
    "quotePreview.switchVersion.engineerOnly",
    "review.create",
    "worksite.accountManager.transfer",
    "worksite.address.overridePhaseGate",
    "sunrunAutomation.readEventLogs",
    "sunrunAutomation.updateSettings",
    "task.forceComplete",
    "ticket.delete",
    "user.code",
    "view.read.contractorTraining",
    "workflow.modifyDraft",
    "workflow.publish",
    "reviewBidGuide.modifyDraft",
    "reviewBidGuide.publish",
  ],

  REMI_DESIGNER: ["aiFeedback.viewLink", "ticket.delete", "user.design"],

  REMI_COMPLIANCE: [
    "contractorProfile.activate",
    "person.read.basic",
    "region.viewAssignablePersons",
    "notificationSettings.view",
    "permittingEntity.view",
    "propertyAddress.read",
    "region.read",
  ],

  REMI_COMPLIANCE_ADMIN: ["contractorDocumentExemption.create", "contractorDocumentExemption.delete"],

  REMI_FINANCIAL: [
    "remittance.read.internal",
    "adjuster.review",
    "appeasement.abandon",
    "appeasement.approve",
    "appeasement.confirmPayment",
    "appeasement.create",
    "appeasement.markSent",
    "appeasement.read",
    "appeasement.reject",
    "appeasement.update",
    "billReview.create",
    "creditMemo.create",
    "geographicArea.update.markets",
    "journalEntry.create",
    "ratebook.publish",
    "ratebook.create",
    "ratebook.update",
    "ratebook.readDrafts",
    "laborbook.update",
    "laborbook.readDrafts",
    "loanRateSheet.read",
    "reviewRevenueAutoComplete.read",
    "task.read.finance",
    "task.update.finance",
    "task.update.workOrderConcessionApproval",
    "verifyContractorCostsAutoComplete.read",
    "view.read.finance",
  ],

  REMI_FINANCIAL_ADMIN: [
    "accounting.read",
    "accounting.readReports",
    "accounting.sync",
    "adminTools.salesAdminTools.read",
    "bill.delete",
    "adminTools.invoiceAdminTools.read",
    "adminTools.invoiceAdminTools.update",
    "adminTools.invoiceAdminTools.attachEscrow",
    "creditMemo.delete",
    "financeAdminTools.read",
    "financeAdminTools.update",
    "invoice.void",
    "invoice.delete",
    "journalEntry.delete",
    "lineItemPrice.read",
    "lineItemPrice.create",
    "lineItemPrice.update",
    "loanRateSheet.publish",
    "mcp.query_quickbooks",
    "quote.override",
    "quote.createNonSalesAdjustment",
    "quote.createWhenQuotingIsDisabled",
    "market.create",
    "reviewRevenueAutoComplete.update",
    "market.update",
    "task.read.financeAdmin",
    "task.update.financeAdmin",
    "vendorCredit.delete",
    "verifyContractorCostsAutoComplete.update",
    "view.read.reviewFinancialAdjustments",
  ],

  REMI_QUOTE_ADMIN: ["adminTools.quoteAdminTools.update"],

  CONTRACTOR_ADMIN: [
    "bill.read.own",
    "remittance.read.own",
    "calendar.create",
    "changeOrder.create",
    "changeOrder.read",
    "changeOrder.read.all",
    "channel.read",
    "channel.readOrganization",
    "colorSelection.read",
    "contractorDocuments.uploadCorrection",
    "contractorInternalDetailFields.read",
    "county.read",
    "contractorProfile.read.org",
    "file.update.owned",
    // CONTRACTOR_ADMIN-only. Allows updating files uploaded by other org
    // members on actionable post-install tasks. CONTRACTOR_PRODUCTION must
    // NOT receive this capability — its absence here is the privilege
    // boundary (previously enforced by an explicit role check in file.ts
    // before BONES-187 Phase D).
    "file.update.contractorTaskUpload",
    "contractorProfile.update.org",
    "conversation.read.org",
    "conversationItem.read.org",
    "deadline.read",
    "dropboxSign.access.org",
    "externalContractor.read",
    "geographicArea.read",
    "geographicArea.read.serviceAreas.own",
    "geographicArea.readCountyOverlay",
    "geographicArea.readStateOverlay",
    "geographicArea.update.serviceAreas",
    "serviceArea.capacity.org",
    "serviceArea.pause.org",
    "message.channel.view.contractor",
    "message.defaultChannel.orgScoped",
    "organization.read.own",
    "organization.read.org",
    "organization.readType.contractor",
    "organization.readType.remiInternal",
    "organizationSettings.update",
    "partnerFlow.config.read",
    "person.read.basic",
    "personToContractor.read.org",
    "person.invite",
    "person.update.org",
    "phaseDuration.read",
    "project.photos",
    "project.updateEstimateDate",
    "project.read.org",
    "project.statusUpdate.contractor",
    "project.owner.updateContractorRole",
    "receipt.create",
    "receipt.read",
    "review.create.contractor",
    "blockingTask.responsibleParty.contractor",
    "task.read.contractorComplaint",
    "task.update.contractorAdmin",
    "task.recordAttempt",
    "task.viewAttemptHistory",
    "user.updateStatus",
    "workOrder.read",
    "workOrder.update.own",
    "workOrder.read.all",
    "worksite.read",
  ],

  CONTRACTOR_PRODUCTION: [
    "checklist.update",
    "colorSelection.read",
    "externalContractor.read",
    "file.access.mobile",
    "file.update.owned",
    "finalInspection.read",
    "laborbook.read",
    "navigation.read.tasks",
    "navigation.read.todaysActions",
    "navigation.read.projectMilestones",
    "navigation.read.performance",
    "notificationList.read",
    "notifications.preferences.read",
    "notifications.preferences.mutate",
    "notifications.read",
    "notifications.mutate",
    "opsAssignment.read",
    // Today's plate drops a task whose contractor organization the reader can't see, so without this a crew's
    // own jobs never reach Today. organizationWhere still limits it to the crew's own organization.
    "organization.readType.contractor",
    "project.read",
    "project.read.assigned.production",
    "receipt.create",
    "receipt.read",
    "projectTable.read",
    "projectCategory.read",
    "projectCheckIn.create",
    "projectCheckIn.read",
    "projectCheckIn.delete",
    "scopeOfWorkType.read",
    "task.read",
    "task.read.org",
    "task.update",
    "task.recordAttempt",
    "task.read.contractor",
    "task.update.contractor",
    "view.read.contractorTasks",
    "viewList.read",
    "file.read.contractor",
    "notificationSettings.view",
    "propertyAddress.read",
    "region.read",
  ],

  BUYER_ADMIN: [
    "bid.read",
    "bidContact.read",
    "bidProperties.read",
    "bidRequest.read",
    "bidTable.read",
    "bidTable.update",
    "bid.read.org",
    "channel.read",
    "conversation.read.org",
    "conversation.message.partner.send",
    "conversationItem.read.org",
    "invoice.viewSummary",
    "jobDetails.read",
    "message.channel.view.solarCompany",
    "notificationList.read",
    "notifications.preferences.read",
    "notifications.preferences.mutate",
    "notifications.read",
    "notifications.mutate",
    "organization.read.own",
    "organization.read.org",
    "organization.readType.remiInternal",
    "organization.readType.solarCompany",
    "partnerFlow.read",
    "person.read.basic",
    "phaseDuration.read",
    "project.read.buyer",
    "projectTable.read",
    "projectCategory.read",
    "bidQuote.read.org",
    "quoteVersion.read.org",
    "quoteTab.read",
    "requestForQuote.read.org",
    "roofScope.read",
    "scopeOfWorkType.read",
    "worksite.read",
    "file.read.buyer",
    "buyerData.read",
    "blockingTask.responsibleParty.buyer",
    "notificationSettings.view",
    "permittingEntity.view",
    "person.invite",
    "project.read",
    "propertyAddress.read",
    "region.read",
    "task.read.insurance.assessment",
    "task.read.insurance.approval",
  ],

  INVALID: [],

  NOMAD_INSPECTION: [],
  NOMAD_QA: [],
  NOMAD_CORRECTION: [],

  HOMEOWNER: [
    "blockingTask.create.homeowner",
    "blockingTask.responsibleParty.homeowner",
    "conversation.read.org",
    "conversationItem.read.org",
    "bid.read.owned",
    "project.read.org",
    "homeownerPortal.read",
    "notifications.preferences.read",
    "notifications.preferences.mutate",
    "notifications.read",
    "notifications.mutate",
    "partnerFlow.config.read",
    "propertyAddress.update",
    "propertyAddress.update.owned",
    "propertyAddress.read",
    "projectStatus.update",
    "bidQuote.read.owned",
    "quoteVersion.read.owned",
    "requestForQuote.read.owned",
    "file.read.homeowner",
    "region.read",
    "notificationSettings.view",
    "permittingEntity.view",
    "task.read.homeowner",
    "task.update.homeowner",
  ],

  PARTNER_DEVELOPER: [
    "bid.create.org",
    "bid.viewCreateBid",
    "bid.read.org",
    "bidRequest.read",
    "bidRequest.create",
    "bidRequest.update",
    "bidRequest.updateStatus.org",
    "bidRequest.read.org",
    "conversation.read.org",
    "conversation.message.partner.send",
    "navigation.read.developerPortal",
    "notifications.preferences.read",
    "notifications.preferences.mutate",
    "notifications.read",
    "notifications.mutate",
    "person.read.basic",
    "project.read.org",
    "quote.approve",
    "bidQuote.read.org",
    "quoteVersion.read.org",
    "notificationSettings.view",
    "permittingEntity.view",
    "propertyAddress.read",
    "region.read",
  ],

  INSURANCE_ADJUSTER: [
    "bid.read.org",
    "bidQuote.read.org",
    "bidRequest.read",
    "bidRequest.update",
    "insurance.createAssignment",
    "navigation.read.insuranceMyAssignments",
    "notifications.preferences.read",
    "notifications.preferences.mutate",
    "notifications.read",
    "notifications.mutate",
    "organization.read.org",
    "organization.readType.solarCompany",
    "partnerFlow.read",
    "person.read.basic",
    "project.read.org",
    "quoteVersion.read.org",
    "requestForQuote.read.org",
    "task.read",
    "task.read.org",
    "task.update",
    "task.read.insurance.assessment",
    "task.read.insurance.approval",
    "task.update.insurance.approval",
    "view.read.insurance",
    "file.read.adjuster",
    "permittingEntity.view",
    "propertyAddress.read",
    "region.read",
  ],

  FIELD_WORKER: [
    "checklist.update",
    "escalationHub.readFieldStatus",
    "file.access.mobile",
    "file.read.owned",
    "file.update.owned",
    "project.read",
    "project.read.assigned",
    "notifications.preferences.read",
    "notifications.preferences.mutate",
    "notifications.read",
    "notifications.mutate",
    "region.read",
    "notificationSettings.view",
    "permittingEntity.view",
    "propertyAddress.read",
  ],

  ESTIMATOR: [
    "bid.read",
    "bid.read.all",
    "conversation.view",
    "conversation.list",
    "conversation.sendMessage",
    "conversation.message.partner.send",
    "conversation.sms.homeowner.send",
    "conversation.sms.contractor.send",
    "conversation.update",
    "conversation.create",
    "conversation.read.all",
    "conversationItem.read.all",
    "escalationHub.readBanner",
    "estimate.create",
    "file.update.owned",
    "fileAnnotation.read",
    "fileAnnotation.update",
    "jobDetails.read",
    "message.channel.view.remiInternal",
    "message.create",
    "navigation.read.estimating",
    "notifications.preferences.read",
    "notifications.preferences.mutate",
    "notifications.read",
    "notifications.mutate",
    "organization.read.org",
    "organization.readType.remiInternal",
    "partnerFlow.read",
    "partnerFlow.config.read",
    "person.read.basic",
    "project.read",
    "project.read.all",
    "roofScope.read",
    "task.read",
    "task.read.assigned",
    "task.update",
    "task.read.insurance.assessment",
    "task.update.insurance.assessment",
    "task.recordAttempt",
    "task.recordAttempt.internalReasons",
    "task.viewAttemptHistory",
    "taskStatus.update",
    "view.read.estimating",
    "worksite.read",
    "file.read.estimator",
    "region.read",
    "permittingEntity.view",
    "propertyAddress.read",
    "worksiteView.read",
  ],

  ESTIMATE_REVIEWER: [
    "fileAnnotation.read",
    "fileAnnotation.update",
    "task.read.insurance.estimateReview",
    "task.update.insurance.estimateReview",
  ],

  ESTIMATE_ADMIN: [
    "adminTools.contractorCapabilityGroupings.read",
    "adminTools.contractorCapabilityGroupings.create",
    "adminTools.contractorCapabilityGroupings.update",
    "adminTools.contractorCapabilityGroupings.delete",
    "adminTools.estimateMappings.read",
    "adminTools.estimateQaChecks.read",
    "adminTools.estimateQaChecks.create",
    "adminTools.estimateQaChecks.update",
    "adminTools.estimateQaChecks.delete",
    "task.override.insurance.approval",
  ],
} as const satisfies Record<PermissionRole, readonly Capability[]>;
